import type { UserInfo } from '/#/store';
import type { ErrorMessageMode } from '/#/axios';
import { defineStore } from 'pinia';
import { store } from '/@/store';
import { RoleEnum } from '/@/enums/roleEnum';
import { PageEnum } from '/@/enums/pageEnum';
import { ROLES_KEY, TOKEN_KEY, USER_INFO_KEY, ROUT_KEY } from '/@/enums/cacheEnum';
import { getAuthCache, setAuthCache } from '/@/utils/auth';
import { GetUserInfoModel, LoginParams, AutoLoginParams, GetsendSmsCodeModel, PhoneParams, GetParamModel } from '/@/api/sys/model/userModel';
import { doLogout, getUserInfo, loginApi, GetautoLogin, sendSmsCodeApi, loginByPhoneApi, GetQyParam, GetQyParames } from '/@/api/sys/user';
import { getautoLogin } from '../../api/demo/system';
import { useI18n } from '/@/hooks/web/useI18n';
import { useMessage } from '/@/hooks/web/useMessage';
import { router } from '/@/router';
import { usePermissionStore } from '/@/store/modules/permission';
import { RouteRecordRaw } from 'vue-router';
import { PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic';
import { isArray } from '/@/utils/is';
import { h } from 'vue';
import {  getlogutUrl, getParamsFromHash, } from '../../router/guard/geturlGuard';
interface UserState {
  userInfo: Nullable<UserInfo>;
  token?: string;
  roleList: RoleEnum[];
  sessionTimeout?: boolean;
  lastUpdateTime: number;
  routlist:any[];
  type: number;
  param:  Nullable<UserInfo>;
  qrcode: number;
  logincode: number;
  loginmsg:string;
  routePage: string;
}
const BASE_LOGIN = PageEnum.BASE_LOGIN;
export const useUserStore = defineStore({
  id: 'app-user',
  state: (): UserState => ({
    // user info
    userInfo: null,
    // token
    token: undefined,
    // roleList
    roleList: [],
    // Whether the login expired
    sessionTimeout: false,
    // Last fetch time
    lastUpdateTime: 0,
    routlist: [],
    type: 0,
    param: null,
    qrcode: 0,
    logincode: 2,
    loginmsg:'',
    routePage: '',
  }),
  getters: {
    getUserInfo(): UserInfo {
      return this.userInfo || getAuthCache<UserInfo>(USER_INFO_KEY) || {};
    },
    getToken(): string {
      return this.token || getAuthCache<string>(TOKEN_KEY);
    },
    getRoleList(): RoleEnum[] {
      return this.roleList.length > 0 ? this.roleList : getAuthCache<RoleEnum[]>(ROLES_KEY);
    },
    getSessionTimeout(): boolean {
      return !!this.sessionTimeout;
    },
    getLastUpdateTime(): number {
      return this.lastUpdateTime;
    },
    getRoutList(): any[] {
      return this.routlist || getAuthCache<any>(ROUT_KEY);
    },
    getType(): number{
      return this.type;
    },
    getParam(): UserInfo{
      return this.param || getAuthCache<UserInfo>(USER_INFO_KEY) || {};
    },
    getQrcode(): number{
      return this.qrcode;
    },
    getLogincode(): number{
      return this.logincode;
    },
    getLoginmsg(): string{
      return this.loginmsg;
    },
    getRoutePage():string {
      return this.routePage;
    },
  },
  actions: {
    setToken(info: string | undefined) {
      this.token = info ? info : ''; // for null or undefined value
      setAuthCache(TOKEN_KEY, info);
    },
    setRoleList(roleList: RoleEnum[]) {
      this.roleList = roleList;
      setAuthCache(ROLES_KEY, roleList);
    },
    setUserInfo(info: UserInfo | null) {
      this.userInfo = info;
      this.lastUpdateTime = new Date().getTime();
      setAuthCache(USER_INFO_KEY, info);
    },
    setSessionTimeout(flag: boolean) {
      this.sessionTimeout = flag;
    },
    setRoutList(info: any[]) {
      this.routlist = info; // for null or undefined value
      setAuthCache(ROUT_KEY, info);
    },
    setType(type:number){
      this.type = type;
    },
    setParam(param: UserInfo | null){
      this.param = param;
    },
    SetQrcode(qrcode:number){
      this.qrcode = qrcode;
    },
    SetLogincode(logincode:number){
      this.logincode = logincode;
    },  
    SetLoginmsg(loginmsg:string){
      this.loginmsg = loginmsg;
    },
    setRoutePage(routePage: string){
      this.routePage = routePage;
    },
    resetState() {
      this.userInfo = null;
      this.token = '';
      this.roleList = [];
      this.sessionTimeout = false;
    },
    /**
     * @description: login
     */
    async login(
      params: LoginParams & {
        goHome?: boolean;
        mode?: ErrorMessageMode;
      },
    ): Promise<GetUserInfoModel | null> {
      try {
        const { goHome = true, mode, ...loginParams } = params;
        const data = await loginApi(loginParams, mode);
        const { token } = data;
        // save token
        this.setToken(token);
        return this.afterLoginAction(goHome);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async loginByPhone(
      params: PhoneParams & {
        goHome?: boolean;
        mode?: ErrorMessageMode;
      },
    ): Promise<GetUserInfoModel | null> {
      try {
        const { goHome = true, mode, ...loginParams } = params;
        const data = await loginByPhoneApi(loginParams, mode);
        const { token } = data;
        // save token
        this.setToken(token);
        return this.afterLoginAction(goHome);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async verificationlogin(
      params: LoginParams & {
        goHome?: boolean;
        mode?: ErrorMessageMode;
      },
    ): Promise<GetsendSmsCodeModel | null> {
      try {
        const { goHome = true, mode, ...loginParams } = params;
        const data_code = await sendSmsCodeApi(loginParams, mode);
        return Promise.resolve(data_code);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async WeixingetQyParam(): Promise<GetParamModel | null> {
      try {
        let data = await GetQyParam();
        this.setParam(data);
        return Promise.resolve(data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async WeixingetQyParames(): Promise<GetParamModel | null> {
      try {
        let data = await GetQyParames();
        this.setParam(data);
        return Promise.resolve(data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async GetautoLoginAction(
      params: AutoLoginParams & {
        mode?: ErrorMessageMode;
      },
    ) {
      try {
        const { mode, ...loginParams } = params;
        const data = await GetautoLogin(loginParams, mode);
        const { token } = data;
        this.setToken(token);
        // return this.afterLoginAction(goHome);
      } catch (error) {
        location.href = '/login#/login';
      }
    },
    async afterLoginAction(goHome?: boolean): Promise<GetUserInfoModel | null> {
      // debugger
      console.log(this.token);
      if (!this.getToken) return null;
      // get user info
      const userInfo = await this.getUserInfoAction();
      const sessionTimeout = this.sessionTimeout;
      if (sessionTimeout) {
        this.setSessionTimeout(false);
      } else {
        const permissionStore = usePermissionStore();
        const get_logUrl:any = getlogutUrl('url');
        let internal:any = getParamsFromHash();
        var url = window.document.location.href.toString();
        let that =this;
        if(url.indexOf('?_t') !== -1){
          console.log( internal.url, ' internal.url');
          if(internal.url.includes("oa")){
            var matched = internal.url.match(/(https?:\/\/[^/]+)/);
            window.location.href = matched[1] + "?token=" + that.getToken;
          }else{
            window.location.href = internal.url+ "?token=" + that.getToken; 
          }  
        }else if (get_logUrl !== undefined) { 
          setTimeout(function (){
            window.location.href = decodeURIComponent(get_logUrl.url) + "?token=" + that.getToken;   
          },1000)
        }else{
          if (!permissionStore.isDynamicAddedRoute) {
            const routes = await permissionStore.buildRoutesAction();
            routes.forEach((route) => {
              router.addRoute(route as unknown as RouteRecordRaw);
            });
            router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);
            permissionStore.setDynamicAddedRoute(true);
          }
          goHome && (await router.replace(userInfo?.homePath || PageEnum.BASE_HOME));
        }
      }
      return userInfo;
    },
    async getUserUrl(data) {
      const userInfo = await getautoLogin(data);
      return userInfo;
    },
    // async GetautoLoginAction(goHome?: boolean, dataStr) {
    //   const data = await GetautoLogin(dataStr);
    //   const { token } = data;
    //   this.setToken(token);
    //   return this.afterLoginAction(goHome);
    //   //return userInfo;
    // },
    async getUserInfoAction(): Promise<UserInfo | null> {
      if (!this.getToken) return null;
      const userInfo = await getUserInfo();
      // console.log(userInfo,'userInfouserInfouserInfouserInfo');
      // this.getType = 
      const { roles = [] } = userInfo;
      if (isArray(roles)) {
        const roleList = roles.map((item) => item.value) as RoleEnum[];
        this.setRoleList(roleList);
      } else {
        userInfo.roles = [];
        this.setRoleList([]);
      }
      this.setUserInfo(userInfo);
      return userInfo;
    },

    async getVerificationToken() {
      
      if (!this.getToken) return null;
      const userInfo = await getUserInfo();
      const get_logUrl = getlogutUrl('url');
      const { roles = [] } = userInfo;
      if (isArray(roles)) {
        // 说明token正常
        //console.log(this.getToken, 'this.getToken');
        window.location.href = get_logUrl.url + "/?token=" + this.getToken;
      } else {
        // 说明token错误
        window.localStorage.clear();
        router.push(PageEnum.BASE_LOGIN);
      }
    },
    /**
     * @description: logout
     */
    async logout(goLogin = false) {
      if (this.getToken) {
        try {
          await doLogout();
        } catch {
          console.log('注销Token失败');
        }
      }
      this.setToken(undefined);
      this.setSessionTimeout(false);
      this.setUserInfo(null);
      const get_logUrl = getlogutUrl('url');
      var url = window.document.location.href.toString();
      if(url.indexOf('?_t') !== -1){
        let internal = getParamsFromHash();
        goLogin && router.push(PageEnum.BASE_LOGIN+'?url='+internal.url);  
      }else if (get_logUrl !== undefined) {
        // const domain = window.location.protocol + "//" + window.location.hostname;
        //console.log(domain + "/login?url=" + get_logUrl.url, 'domaindomain');
        goLogin && router.push(PageEnum.BASE_LOGIN+'?url='+get_logUrl.url);  
      }else{
        goLogin && router.push(PageEnum.BASE_LOGIN);
      }
    },

    /**
     * @description: Confirm before logging out
     */
    confirmLoginOut() {
      const { createConfirm } = useMessage();
      const { t } = useI18n();
      createConfirm({
        iconType: 'warning',
        title: () => h('span', t('sys.app.logoutTip')),
        content: () => h('span', t('sys.app.logoutMessage')),
        onOk: async () => {
          await this.logout(true);
        },
      });
    },
  },
});

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store);
}
